import { DatePipe, formatDate } from "@angular/common";
import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { Title } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { EmailMultipleAddress } from "@models/email-templates/email-multiple-address";
import { EmailPayloadBase } from "@models/email-templates/email-payload-base";
import { EmailSendRequest } from "@models/email-templates/email-send-request";
import { EmailTemplate } from "@models/email-templates/email-template";
import { NgbDropdown, NgbDropdownItem, NgbDropdownMenu, NgbDropdownToggle } from "@ng-bootstrap/ng-bootstrap";
import { DocumentsApiService } from "@shared/services/documents-api.service";
import { DocumentCommonService } from "@shared/services/documents-common.service";
import { DocumentsService } from "@shared/services/documents.service";
import { EmailsApiService } from "@shared/services/emails-api.service";
import { FilesApiService } from "@shared/services/files-api.service";
import { FilesService } from "@shared/services/files.service";
import { MenuService } from "@shared/services/menu.service";
import { downloadBase64File } from "@shared/utils/download-base64-file";
import { getPageTitle } from "@shared/utils/get-page-title";
import { Guid } from "@shared/utils/guid.helper";
import { getGenerateDocumentPath } from "@shared/utils/route-path-utils";
import { ToastrService } from "ngx-toastr";
import { catchError, forkJoin, of, tap } from 'rxjs';
import { Document } from "@models/documents/document";
import { DocumentNotificationDetails } from '@models/documents/document-notification-details';
import { SendOption } from '@models/annual-statements/annual-statement-enums';
import { DocumentSubFolderName } from "@models/common/document-sub-folder-name.enum";
import { EntityType } from "@models/common/entity-type.enum";
import { HttpErrorCode } from "@models/requests/http-error-code.enum";
import { FilesResponse } from "@models/ui-models/files";
import { ESignBody } from '@models/documents/sign-documents';
import { CheckboxComponent } from "@shared/controls/checkbox/checkbox.component";
import {
  AgTagEntityTitleComponent
} from "@shared/components/grid/ag-cells/common/ag-tag-entity-title/ag-tag-entity-title.component";
import { LoaderComponent } from "@shared/components/commons/loader/loader.component";
import {
  MultipleInputEmailComponent
} from "@shared/controls/multiple-input-email/multiple-input-email.component";
import { PageTitleComponent } from "@shared/components/page-title/page-title.component";
import { TagComponent } from "@shared/components/commons/tag/tag.component";
import { EmailPreviewComponent } from "@shared/components/email-preview/email-preview.component";
import {
  DocumentsListAccordionComponent
} from "../../../../generate-documents/components/documents-list-accordion/documents-list-accordion.component";

@Component({
  selector: 'app-asic-invoice-page',
  standalone: true,
  imports: [
    PageTitleComponent,
    LoaderComponent,
    AgTagEntityTitleComponent,
    CheckboxComponent,
    MultipleInputEmailComponent,
    ReactiveFormsModule,
    EmailPreviewComponent,
    DocumentsListAccordionComponent,
    NgbDropdown,
    NgbDropdownItem,
    NgbDropdownMenu,
    NgbDropdownToggle,
    DatePipe,
    TagComponent
  ],
  templateUrl: 'asic-invoice-page.component.html',
  styleUrl: 'asic-invoice-page.component.scss',
  providers: [EmailsApiService],
})
export class AsicInvoicePageComponent implements OnInit {
  emailTemplatesService = inject(EmailsApiService);
  documentCommonService = inject(DocumentCommonService);
  documentsService = inject(DocumentsService);
  documentsApiService = inject(DocumentsApiService);
  filesService = inject(FilesService);
  filesApiService = inject(FilesApiService);

  route: ActivatedRoute = inject(ActivatedRoute);
  router = inject(Router);
  menuService = inject(MenuService);
  titleService = inject(Title);
  toastr = inject(ToastrService);
  destroyRef = inject(DestroyRef);

  readonly entityType = EntityType.Company;
  readonly DocumentSubFolderName = DocumentSubFolderName;
  // readonly filesToAttachInfo = 'You may upload up to 10 PDF files, including your own invoice, with a total size limit of 2 MB.';

  isSidebarCollapsed = signal(false);
  isLoading = signal(false);
  documentId: string | null = null;
  document: Document | null = null;
  files: FilesResponse | null = null;
  sendOptionEmail = SendOption.Email;
  successMessage = '';
  template!: EmailTemplate;
  payloadModel!: EmailPayloadBase;

  form = new FormGroup({
    includeAttachedFiles: new FormControl<boolean>(true, { nonNullable: true }),
    emails: new FormControl<EmailMultipleAddress[]>([], { nonNullable: true, validators: [Validators.minLength(1)] })
  });

  ngOnInit(): void {
    this.menuService.setMenuState(true);
    this.documentId = this.route.snapshot.paramMap.get('documentId') ?? null;
    this.tryLoadAsicInvoice();
  }

  onChangeEmail(emailData: Partial<ESignBody>): void {
    this.template = emailData.emailTemplate as unknown as EmailTemplate;
    this.payloadModel = emailData.payloadModel as unknown as EmailPayloadBase;
  }

  sendEmail(): void {
    if (this.form.invalid) {
      this.toastr.error('Please fill required fields', 'Error');
      this.form.markAllAsTouched();
      return;
    }

    this.isLoading.set(true);

    const emailRequests = this.form.controls.emails.value.map(emailAddress => {
      const emailSendRequest: EmailSendRequest = {
        to: emailAddress.email,
        payloadModel: this.payloadModel,
        template: this.template,
        documentId: this.documentId!,
        includeAttachedFiles: this.form.controls.includeAttachedFiles.value,
        logo: undefined,
        brand: null
      };

      return this.emailTemplatesService.sendEmailTemplate(emailSendRequest);
    });

    forkJoin(emailRequests).subscribe({
      next: (results) => {
        this.toastr.success('Email(s) has been successfully sent', 'Success');

        const emails = this.form.controls.emails.value.map(e => e.email);
        this.successMessage = this.getSuccessSentMessage(emails);
        this.isLoading.set(false);

        const notificationDetails: DocumentNotificationDetails = {
          sentOn: new Date(),
          sentToEmails: this.form.controls.emails.value.map(email => email.email)
        };
        this.document!.notificationDetails = notificationDetails;
        this.documentsApiService.updateNotificationDetails(this.documentId!, notificationDetails).subscribe();
      },
      error: (error) => {
        this.toastr.error('Error sending email', 'Error');
        this.form.enable();
        this.isLoading.set(false);
      }
    });
  }

  tryLoadAsicInvoice(documentId = this.documentId): void {
    if (!documentId || !Guid.isValidGuid(documentId)) {
      void this.router.navigate(['/companies']);
      this.toastr.error('Link is not valid.');
      return;
    }

    this.titleService.setTitle(getPageTitle('ASIC Invoice'));
    this.loadAsicInvoice();
  }

  loadAsicInvoice(documentId = this.documentId!): void {
    this.isLoading.set(true);

    this.documentsApiService.getById(documentId)
      .subscribe({
        next: (document) => {
          if (!Document.isDocumentInvoice(document)) {
            void this.router.navigate(getGenerateDocumentPath(documentId));
            return;
          }

          this.document = new Document(document);
          this.loadDocumentFiles();

          const notificationDetails = this.document.notificationDetails;
          if (notificationDetails?.sentToEmails && notificationDetails.sentToEmails.length > 0) {
            this.successMessage = this.getSuccessSentMessage(notificationDetails.sentToEmails, notificationDetails.sentOn);
          }
          this.isLoading.set(false);
        },
        error: (error) => {
          console.warn('[loadAsicInvoice] error: ', error);

          if ((error.status as HttpErrorCode) === HttpErrorCode.NOT_FOUND) {
            this.toastr.error(`Invoice ${ documentId } not found`, 'Error!');
          } else {
            this.toastr.error(`${ error.message }`, 'Error!');
            console.error(error);
          }
        }
      });
  }

  loadDocumentFiles(documentId = this.documentId!): void {
    this.filesApiService.getFiles(documentId)
      .subscribe({
        next: (files) => {
          this.files = files;
        },
        error: (error) => {
          console.warn('[loadDocumentFiles] error: ', error);
        }
      });
  }

  deleteFile(fileName: string): void {
    this.documentCommonService.deleteFile(this.document!.documentId, fileName, DocumentSubFolderName.FileAttached).pipe(
      tap((result: boolean) => {
        if (result)
          this.loadDocumentFiles(this.document!.documentId);
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  downloadFile(fileName: string, subFolder: string) {
    this.documentCommonService.downloadFile(this.document!.documentId, fileName, subFolder);
  }

  downloadCombinedPDF(subFolder: string): void {
    this.filesApiService
      .downloadFolder(this.document!.documentId, true, subFolder)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.error(error);
          this.toastr.error("Download 'Generated documents.pdf' error", "Error");
          return of('ERR');
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((res) => {
        if (res !== 'ERR') {
          downloadBase64File(res, `Generated documents.pdf`);
        }
      });
  }

  downloadZIP(subFolder: string): void {
    this.filesApiService
      .downloadFolder(this.document!.documentId, false, subFolder)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.error(error);
          this.toastr.error("Download 'Generated documents.zip' error", "Error");
          return of('ERR');
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((res) => {
        if (res !== 'ERR') {
          downloadBase64File(res, "Generated documents.zip");
        }
      });
  }

  openUploadFileModal(subfolder: DocumentSubFolderName): void {
    const fileUploadConfig = { allowedMaxFileSizeMB: 30, acceptedFormatsList: [] };
    this.filesService.openUploadFilesModal(this.document!.documentId!, subfolder, fileUploadConfig)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadDocumentFiles(this.document!.documentId));
  }

  getSuccessSentMessage(emails?: string[], date?: Date): string {
    if (!emails?.length) return '';

    const emailList = emails.join(', ');
    const currentDate = formatDate(date ?? new Date(), 'dd MMM yyyy', 'en-US');
    return `The invoice has been emailed to ${ emailList } on ${ currentDate }`;
  }

}
